<template>
  <div class="card">
    <b-card-header v-if="$hasPermission('manage_roles')">
      <b-button
        v-b-modal.modal-add-role
        variant="success"
        @click="addNewRole"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        /> {{ $t('usersettings.newRole') }}
      </b-button>
    </b-card-header>

    <div class="table-responsive mb-0">
      <table class="table b-table table-striped table-hover">
        <thead>
          <tr>
            <th style="width: 200px">
              {{ $t('usersettings.role') }}
            </th>
            <th>{{ $t('usersettings.permissions') }}</th>
            <th
              class="text-right"
              style="width: 150px"
            >
              {{ $t('payments.actions') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="!isBusy">
          <tr
            v-for="role in sortedItems"
            :key="role.id"
          >
            <td class="align-top">
              <div class="font-weight-bold">
                {{ role.display_name }}
              </div>
              <b-badge
                v-if="role.is_system"
                variant="light-secondary"
                class="mt-25"
              >
                {{ $t('usersettings.systemRole') }}
              </b-badge>
            </td>
            <td>
              <div
                class="d-flex flex-wrap"
                style="gap: 4px;"
              >
                <b-badge
                  v-for="perm in (role.permissions || [])"
                  :key="perm"
                  variant="light-primary"
                  style="font-size: 0.75rem; text-transform: none;"
                >
                  {{ formatPermissionName(perm) }}
                </b-badge>
                <span
                  v-if="!(role.permissions && role.permissions.length)"
                  class="text-muted italic small"
                >
                  {{ $t('usersettings.noPermissionsAssigned') }}
                </span>
              </div>
            </td>
            <td class="text-right align-top">
              <template v-if="!role.is_system && $hasPermission('manage_roles')">
                <b-button
                  size="sm"
                  variant="flat-primary"
                  @click="editRole(role)"
                >
                  {{ $t('apps.edit') }}
                </b-button>
                <b-button
                  size="sm"
                  variant="flat-danger"
                  @click="deleteRole(role)"
                >
                  {{ $t('usersettings.delete') }}
                </b-button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-modal
      v-if="$hasPermission('manage_roles')"
      id="modal-add-role"
      scrollable
      hide-footer
      size="lg"
      header-class="border-0 pb-0"
    >
      <add-role-modal
        :role-data="selectedRole"
        :is-edit="isEdit"
        @saved="handleRoleSaved"
      />
    </b-modal>
  </div>
</template>

<script>
import i18n from '@/libs/i18n'
import { permissionLabel } from '@/libs/i18n/display'

import AddRoleModal from '@ps_views/user-settings/AddRoleModal.vue'

export default {
  components: { AddRoleModal },
  data() {
    return {
      isBusy: false,
      items: [],
      selectedRole: {},
      isEdit: false,
      sortBy: 'display_name',
      sortDirection: 'asc',
    }
  },
  computed: {
    sortedItems() {
      return [...this.items].sort((a, b) => {
        // 1. Keep System Roles at the very top
        if (a.is_system && !b.is_system) return -1
        if (!a.is_system && b.is_system) return 1

        // 2. Standard sorting for custom roles
        const modifier = this.sortDirection === 'asc' ? 1 : -1

        const valA = (a[this.sortBy] ?? '').toString().toLowerCase()
        const valB = (b[this.sortBy] ?? '').toString().toLowerCase()

        return valA.localeCompare(valB) * modifier
      })
    },
  },
  created() { this.fetchRoles() },
  methods: {
    fetchRoles() {
      this.isBusy = true
      this.$http.get('/api/publisher-roles').then(res => {
        this.items = res.data
      })
        .catch(errors => {
          this.items = []
          this.alertApiError(errors, i18n.t('usersettings.failedToLoadRoles'))
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    // Helper to make "view_apps" look like "View Apps"
    formatPermissionName(str) {
      if (!str) return ''
      return permissionLabel(str)
    },
    addNewRole() {
      this.isEdit = false
      this.selectedRole = { display_name: '', permissions: [] }
    },
    editRole(role) {
      this.isEdit = true
      this.selectedRole = {
        ...role,
        permissions: [...(role.permissions || [])],
      }
      this.$bvModal.show('modal-add-role')
    },
    deleteRole(role) {
      // Use the full options object for msgBoxConfirm
      this.$bvModal.msgBoxConfirm(
        i18n.t('usersettings.areYouSureYouWantToDeleteTheRole', { value1: role.display_name }),
        {
          title: i18n.t('usersettings.deleteRoleTitle', { value1: role.display_name }),
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: i18n.t('usersettings.deleteRole'),
          cancelTitle: i18n.t('usersettings.cancel'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(value => {
        if (value) {
          this.showLoading()
          this.$http.delete(`/api/publisher-roles/${role.id}`)
            .then(() => {
              this.fetchRoles()
              this.alertSuccess(i18n.t('usersettings.roleDeletedSuccessfully'))
            })
            .catch(errors => {
              this.alertApiError(errors, i18n.t('usersettings.roleDeleteFailed'))
            })
            .finally(() => {
              this.hideLoading()
            })
        }
      })
    },
    handleRoleSaved() {
      this.$bvModal.hide('modal-add-role')
      this.fetchRoles()
    },
  },
}
</script>
